import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/Layout';
import { RevoltGlassButton } from '../../components/RevoltGlassButton';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigationManager } from '../../core';
import './BetaInviteSection.css';

/**
 * BetaInviteSection
 * - Validates beta invite code from URL (?code=XXX)
 * - If not logged in: require auth, then return to this page to redeem
 * - If logged in: show "Claim beta access", then redeem and redirect to /app
 */
export function BetaInviteSection() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const navigationManager = useNavigationManager();
  const { isAuthenticated, user, loading: authLoading, authChecked, checkAuthStatus } = useAuth();

  const code = searchParams.get('code')?.trim() || '';

  const [status, setStatus] = useState('checking'); // checking | invalid | need-login | ready | redeeming | success | error
  const [message, setMessage] = useState('');
  const [authSynced, setAuthSynced] = useState(false);
  const requestedAuthRef = useRef(false);

  useEffect(() => {
    const sync = async () => {
      try {
        await checkAuthStatus?.();
      } catch (e) {
        // ignore
      } finally {
        setAuthSynced(true);
      }
    };
    sync();
  }, [checkAuthStatus]);

  // Validate code and decide next step
  useEffect(() => {
    if (!code) {
      setStatus('invalid');
      setMessage('Missing invite code. Use the link you received.');
      return;
    }

    const validate = async () => {
      setStatus('checking');
      setMessage('');
      try {
        const res = await fetch('/api/account/beta-invite/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });
        const data = await res.json();

        if (!data.success) {
          setStatus('error');
          setMessage(data.error || 'Failed to validate code.');
          return;
        }

        if (!data.valid) {
          setStatus('invalid');
          setMessage(data.error || 'This invite code is invalid or has expired.');
          return;
        }

        // Valid code
        if (!authChecked || authLoading || !authSynced) {
          setStatus('checking');
          setMessage('Checking authentication...');
          return;
        }

        if (!isAuthenticated) {
          const returnUrl = `/beta-invite?code=${encodeURIComponent(code)}`;
          if (!requestedAuthRef.current) {
            requestedAuthRef.current = true;
            navigationManager.requireAuth(returnUrl, { code });
          }
          setStatus('need-login');
          setMessage('Sign in or create an account to claim your beta access.');
          return;
        }

        setStatus('ready');
        setMessage('');
      } catch (err) {
        setStatus('error');
        setMessage('Unable to validate the invite. Please try again.');
      }
    };

    validate();
  }, [code, authChecked, authLoading, isAuthenticated, authSynced, navigationManager]);

  const handleRedeemClick = async () => {
    if (!code) return;
    setStatus('redeeming');
    setMessage('Claiming beta access...');
    try {
      const res = await fetch('/api/account/beta-invite/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code }),
      });
      const data = await res.json();

      if (res.status === 403) {
        setStatus('invalid');
        setMessage(data.error || 'This code is not valid for your account.');
        return;
      }

      if (!res.ok || !data.success) {
        setStatus('error');
        setMessage(data.error || 'Failed to claim beta access.');
        return;
      }

      setStatus('success');
      setMessage("You're a beta tester! Redirecting...");
      setTimeout(() => {
        navigate('/app', { replace: true });
      }, 1200);
    } catch (err) {
      setStatus('error');
      setMessage('Unable to claim beta access. Please try again.');
    }
  };

  const renderContent = () => {
    if (status === 'checking') {
      return (
        <div className="bi-card-body">
          <div className="loading-spinner" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
          <p className="bi-muted">{message || 'Checking invite...'}</p>
        </div>
      );
    }

    if (status === 'invalid' || status === 'error') {
      return (
        <div className="bi-card-body">
          <div className={`alert ${status === 'invalid' ? 'alert-warning' : 'alert-error'}`}>
            {message}
          </div>
        </div>
      );
    }

    if (status === 'need-login') {
      return (
        <div className="bi-card-body">
          <div className="alert alert-info">{message}</div>
          <p className="bi-muted">You will return here after signing in to claim your access.</p>
        </div>
      );
    }

    if (status === 'ready') {
      return (
        <div className="bi-card-body">
          <div className="alert alert-info" style={{ marginBottom: '16px' }}>
            Signed in as {user?.email}. Claim beta access for this account.
          </div>
          <RevoltGlassButton
            variant="accent"
            size="md"
            onClick={handleRedeemClick}
            style={{ width: '100%' }}
          >
            Claim Beta Access
          </RevoltGlassButton>
        </div>
      );
    }

    if (status === 'redeeming') {
      return (
        <div className="bi-card-body">
          <div className="loading-spinner" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
          <p className="bi-muted">{message}</p>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="bi-card-body">
          <div className="alert alert-success">{message}</div>
        </div>
      );
    }

    return (
      <div className="bi-card-body">
        <div className="alert alert-error">{message || 'Something went wrong.'}</div>
      </div>
    );
  };

  return (
    <MainLayout contentClassName="main-content--beta-invite">
      <div className="beta-invite-container">
        <div className="beta-invite-card">
          <h1 className="bi-title">Beta Invite</h1>
          <p className="bi-subtitle">Use your invite code to get beta access</p>
          {renderContent()}
        </div>
      </div>
    </MainLayout>
  );
}

export default BetaInviteSection;
